// WidgetRosterInviteRow — Oct 2026 (Randy, Widget Roster Stage 2).
//
// A roster invitation, rendered inside the InvitesBanner that already carries
// collaborator invitations on the Dashboard and the Fan Portal. One place for
// invitations to land; two kinds of invitation inside it.
//
// This is a DIFFERENT grant from a collaborator invite. Accepting gives the
// business nothing on your silo and gives you nothing on theirs — it only
// lists your silo on their widget. What it does cost is your fuel, so the
// acknowledgement has to be read and ticked before Accept will fire, the same
// way the child-household invite makes its consent panel unavoidable.
import { useState } from "react";
import { Storefront } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";

export const WidgetRosterInviteRow = ({ inv, copy, onDone }) => {
  const [ack, setAck] = useState(false);
  const [busy, setBusy] = useState(false);

  const respond = async (action) => {
    setBusy(true);
    try {
      await api.post(`/me/widget-invitations/${inv.id}/${action}`,
        action === "accept" ? { sponsor_ack: true } : {});
      toast[action === "accept" ? "success" : "message"](
        action === "accept"
          ? `You're on ${inv.host_silo_name || "their"} website now.`
          : "Invitation declined.");
      onDone(inv.id);
    } catch (e) {
      const d = e.response?.data;
      toast.error((typeof d?.detail === "string" && d.detail) || d?.detail_obj?.message
        || "Could not respond to that invitation.");
    } finally { setBusy(false); }
  };

  return (
    <div className="bg-[#F8F4E8] border border-[#E5C77F] rounded-lg px-4 py-3"
      data-testid={`widget-roster-invite-row-${inv.id}`}>
      <div className="flex items-start gap-3">
        <Storefront size={18} weight="duotone" className="text-[#C19A4A] mt-0.5 shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-sm text-[#1C1B1A]">
            <strong>{inv.host_silo_name || "A business"}</strong> would like{" "}
            <strong>“{inv.member_silo_name}”</strong> on their website, so their
            visitors can pick you and ask you directly.
          </div>
          <div className="mt-2 bg-white/70 border border-[#E5C77F] rounded-md p-3">
            <p className="text-[11px] text-[#6B6A68] leading-relaxed">{copy}</p>
            <label className="flex items-start gap-2 mt-2 cursor-pointer">
              <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)}
                className="mt-0.5" data-testid={`widget-roster-invite-ack-${inv.id}`} />
              <span className="text-[11px] text-[#1C1B1A]">
                I understand these questions use my Silo fuel, and I can leave at any time.
              </span>
            </label>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button onClick={() => respond("accept")} disabled={busy || !ack}
            data-testid={`widget-roster-invite-accept-${inv.id}`}
            className="text-xs px-3 py-1.5 rounded-md bg-[#4A5D4E] text-[#FDFCF8] hover:bg-[#3A4A3E] disabled:opacity-60">
            Accept
          </button>
          <button onClick={() => respond("decline")} disabled={busy}
            data-testid={`widget-roster-invite-decline-${inv.id}`}
            className="text-xs px-3 py-1.5 rounded-md border hairline text-[#6B6A68] hover:border-[#C16E5A] hover:text-[#C16E5A] disabled:opacity-60">
            Decline
          </button>
        </div>
      </div>
    </div>
  );
};

export default WidgetRosterInviteRow;
